import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import dinersclub from './brands/dinersclub.png'
import discover from './brands/discover.png'
import jcb from './brands/jcb.png'
import maestro from './brands/maestro.png'
import mastercard from './brands/mastercard.png'
import visa from './brands/visa.png'

/** OnlyFans' card brand images (./<brand>@2x.png), by lower-cased brand without spaces. */
const BRANDS: Record<string, { src: string; alt: string }> = {
  visa: { src: visa, alt: 'Visa' },
  mastercard: { src: mastercard, alt: 'MasterCard' },
  maestro: { src: maestro, alt: 'Maestro' },
  dinersclub: { src: dinersclub, alt: 'Diners Club' },
  discover: { src: discover, alt: 'Discover' },
  jcb: { src: jcb, alt: 'JCB' },
}

const brandKey = (brand: string) => brand.replace(/\s+/g, '').toLowerCase()

/** A card brand's logo; OnlyFans' generic card icon for brands it has no image for. */
export function BrandIcon({ brand }: { brand: string }) {
  const b = BRANDS[brandKey(brand)]
  if (b) return <img className="ofc-brand-icon" src={b.src} alt={b.alt} />
  return (
    <span className="ofc-brand-icon is-generic" aria-hidden="true">
      <FeatherIcon name="credit-card" size={18} />
    </span>
  )
}

/** CreditCards: the six accepted brands under the statement note. */
export function CreditCardLogos() {
  return (
    <div className="ofc-logos">
      {Object.entries(BRANDS).map(([k, b]) => (
        <img key={k} src={b.src} alt={b.alt} />
      ))}
    </div>
  )
}

/** OnlyFans' AlertOF: a bordered box with the alert / info icon on the left. */
export function Alert({ variant = 'info', loading, children }: { variant?: 'info' | 'danger'; loading?: boolean; children: ReactNode }) {
  return (
    <div className={`ofc-alert is-${variant}`} role={variant === 'danger' ? 'alert' : undefined}>
      <span className="ofc-alert-icon" aria-hidden="true">
        <FeatherIcon name={loading ? 'loader' : variant === 'danger' ? 'alert-triangle' : 'info'} size={22} />
      </span>
      <div className="ofc-alert-text">{children}</div>
    </div>
  )
}

/**
 * The one-line grey note under a control that only works on onlyfans.com
 * (card processor, captcha, payment redirect), with a link opening that page.
 */
export function OfOnly({ text, href }: { text: string; href: string }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  return (
    <p className="ofc-only">
      <span>{text}</span>{' '}
      <a href={href} target="_blank" rel="noopener noreferrer">
        {t('only.open')}
        <FeatherIcon name="external-link" size={12} />
      </a>
    </p>
  )
}

/** A link to an onlyfans.com page, in a new tab. */
export function OfLink({ href, children }: { href: string; children?: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="ofc-link">
      {children}
    </a>
  )
}

/** EmptyPlaceholder with OnlyFans' "payments" bag icon. */
export function EmptyPayments({ text, children }: { text: string; children?: ReactNode }) {
  return (
    <div className="ofc-empty">
      <FeatherIcon name="shopping-bag" size={56} />
      <span>{text}</span>
      {children}
    </div>
  )
}

export interface MenuItem {
  label: string
  onClick?: () => void
  danger?: boolean
  disabled?: boolean
  divider?: boolean
  /** A grey line under the item (why it is disabled). */
  note?: ReactNode
}

/** OnlyFans' DropdownWrapper behind the "…" button (m-btn-horizontal-dots). */
export function DotsMenu({ label, items, disabled }: { label: string; items: MenuItem[]; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false)
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', close)
    window.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', close)
      window.removeEventListener('keydown', esc)
    }
  }, [open])
  return (
    <div className="ofc-menu-wrap" ref={ref}>
      <button
        type="button"
        className="ofc-icon-btn"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <FeatherIcon name="more-horizontal" size={22} />
      </button>
      {open && (
        <div className="ofc-menu" role="menu">
          {items.map((it) => (
            <div key={it.label} role="none">
              {it.divider && <div className="ofc-menu-divider" />}
              <button
                type="button"
                role="menuitem"
                className={`ofc-menu-item ${it.danger ? 'is-danger' : ''}`}
                disabled={it.disabled}
                onClick={() => {
                  setOpen(false)
                  it.onClick?.()
                }}
              >
                {it.label}
              </button>
              {it.note && <div className="ofc-menu-note">{it.note}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
